import { createSerializationAdapter } from "@tanstack/react-router";
import { Result, TaggedError } from "better-result";
import { z } from "zod/v4";
import type { components } from "#/schema";

type ProblemDetails = components["schemas"]["ProblemDetails"];

/** The RFC 7807 body every failed API call carries; extra members (field errors) pass through. */
export const problemDetailsSchema = z
  .object({
    type: z.string().nullish(),
    title: z.string().nullish(),
    status: z.number().nullish(),
    detail: z.string().nullish(),
    instance: z.string().nullish(),
  })
  .loose();

/**
 * Pure (client-safe) API error helpers. Kept out of any `*.server.ts` file so they can be
 * imported by server-function modules that are reachable from client code without tripping
 * TanStack's import-protection.
 */

/** Keeps literal title unions, drops plain `string` (generic ProblemDetails references). */
export type LiteralTitles<U> = U extends string ? (string extends U ? never : U) : never;

/**
 * Codes emitted by middleware instead of a controller action, so no endpoint's `*Errors` enum
 * or response title union carries them. TenantAccessDenied ships in the generated RequestError
 * schema (Extract breaks the build if the backend renames it); the rate-limiter TooManyAttempts
 * is only declared on /auth/login, but its 429 can hit any auth endpoint.
 */
export type MiddlewareErrorTitle =
  | Extract<
      components["schemas"]["RequestError"],
      "TenantAccessDenied" | "MissingTenantHeader" | "TenantWriteRequiresMembership"
    >
  | "TooManyAttempts";

/** A failure that did not come from the API's ProblemDetails contract (e.g. a missing token). */
export class UnknownApiError extends TaggedError("UnknownApiError")<{
  message: string;
}> {}

/**
 * The single error every failed API call surfaces as. `title` carries the backend's
 * ProblemDetails code; `problem` keeps the full body for detail/field-error consumers.
 */
export class ApiError extends TaggedError("ApiError")<{
  title: string;
  status: number;
  problem: ProblemDetails;
  message: string;
}> {}

/**
 * Keeps an {@link ApiError} thrown by a server function an ApiError in the browser. Start's
 * default error serialization keeps only the message, so the client could not tell a documented
 * domain error from a bug.
 */
export const apiErrorSerializationAdapter = createSerializationAdapter({
  key: "ApiError",
  test: (value): value is ApiError => ApiError.is(value),
  toSerializable: ({ title, status, message, problem }) => ({
    title,
    status,
    message,
    problem: JSON.stringify(problem),
  }),
  fromSerializable: ({ title, status, message, problem }) =>
    new ApiError({
      title,
      status,
      message,
      problem: problemDetailsSchema.parse(JSON.parse(problem)),
    }),
});

/**
 * An {@link ApiError} whose `title` is narrowed to the codes the endpoint declares in the
 * generated schema, plus the middleware codes that can hit any endpoint. Produced by
 * {@link toResult}; runtime instances are plain ApiErrors.
 */
export interface TypedApiError<T extends string = string> extends ApiError {
  readonly title: T | MiddlewareErrorTitle | "UnknownError";
}

/** Literal error titles of an openapi-fetch error union (generic 401/500 ProblemDetails drop out). */
export type ErrorTitlesOf<E> = E extends { title?: infer U } ? LiteralTitles<U> : never;

/**
 * Converts a failed openapi-fetch call into a `Response`, for the plain async helpers behind
 * server route handlers (ticket events/attachments, resource files) that signal failure by
 * THROWING a `Response`. Throwing the raw openapi-fetch `error` instead loses both halves of the
 * diagnosis: the browser gets a generic 500 rather than the upstream status, and Sentry captures
 * the bare ProblemDetails as "Object captured as exception with keys: instance, requestId, status,
 * title, type" — grouped under whatever framework frame encloses the throw, so the failure code
 * never reaches the issue title.
 *
 * The body carries the ProblemDetails `title` (the backend's error code), matching the plain-string
 * bodies of the `throw new Response(...)` guards these call sites already use.
 */
export function toErrorResponse(cause: unknown, response: Response): Response {
  const problem = problemDetailsSchema.safeParse(cause);
  const title = (problem.success && problem.data.title) || "UnknownError";
  // `new Response` throws a RangeError outside 200-599, and a 2xx would read as success — anything
  // unexpected becomes 502, since the failure came from the upstream API hop.
  const status = response.status >= 400 && response.status <= 599 ? response.status : 502;
  return new Response(title, { status });
}

/**
 * Converts an awaited openapi-fetch call into a Result whose error union is inferred from
 * the endpoint's schema: `toResult(await client.GET(...))`. Replaces the per-endpoint nswag
 * `*Errors` enums — regenerate the schema and new codes flow into the title union.
 */
export function toResult<T, E>(res: {
  data?: T;
  error?: E;
  response: Response;
}): Result<T, TypedApiError<ErrorTitlesOf<E>>> {
  // Branch on the HTTP status, not on `res.error`: openapi-fetch yields `error: undefined` for
  // non-ok responses with an empty body (e.g. the bare 401 from ASP.NET's JWT middleware), which
  // would otherwise masquerade as a success with `undefined` data.
  if (res.response.ok) {
    // SAFETY: an ok response always carries the endpoint's success body in `data`.
    return Result.ok(res.data as T);
  }
  const problem: ProblemDetails = problemDetailsSchema.catch({}).parse(res.error);
  const title = problem.title || "UnknownError";
  // SAFETY: TypedApiError only narrows `title` at the type level; its runtime shape is ApiError.
  return Result.err(
    new ApiError({
      title,
      status: res.response.status,
      problem,
      message: title,
    }) as TypedApiError<ErrorTitlesOf<E>>,
  );
}
